import React from "react";
import classnames from "classnames";
import moment from "moment";
import momentTimeZone from "moment-timezone";
import { Calendar } from "@instructure/ui-calendar";
import { DateInput } from "@instructure/ui-date-input";
import { IconButton } from "@instructure/ui-buttons";
import {
    IconArrowOpenStartSolid,
    IconArrowOpenEndSolid
} from "@instructure/ui-icons";
import { AccessibleContent } from "@instructure/ui-a11y-content";
import FormInputLoader from "../FormInputLoader/FormInputLoader";
import styles from "./FormDateInput.module.scss";

const getTimezone = () => {
    return momentTimeZone.tz.guess();
};

const locale = "en-us";
const timezone = getTimezone();

//TODO do we need separate ones for restart?

const parseDate = (dateStr) => {
    return moment.tz(
        dateStr,
        [moment.ISO_8601, "llll", "LLLL", "lll", "LLL", "ll", "LL", "l", "L"],
        locale,
        timezone
    );
};

// ** Class working component
class IasFormDateInputRestart extends React.Component {
    state = {
        value: "",
        isShowingCalendar: false,
        todayDate: parseDate(moment()).toISOString(),
        selectedDate: null,
        renderedDate: parseDate(moment()).toISOString(),
        disabledDates: [
            // parseDate("2020-03-11").toISOString(),
        ],
        messages: this.props.messages
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.reset !== this.props.reset && this.props.reset) {
            this.setState({
                value: "",
                selectedDate: null,
                renderedDate: parseDate(moment().toISOString()),
                messages: this.props.messages });
        }
    }

    generateMonth = (renderedDate = this.state.renderedDate) => {
        const date = parseDate(renderedDate)
            .startOf("month")
            .startOf("week");

        return Array.apply(null, Array(Calendar.DAY_COUNT)).map(() => {
            const currentDate = date.clone();
            date.add(1, "days");
            return currentDate;
        });
    };

    formatDate = dateInput => {
        const date = parseDate(dateInput);
        return `${date.format("MMMM")} ${date.format("D")}, ${date.format("YYYY")}`;
    };

    handleChange = (event, { value }) => {
        const newDateStr = parseDate(value).toISOString();

        this.setState(({ renderedDate }, arg2) => ({
            value,
            selectedDate: newDateStr,
            renderedDate: newDateStr || renderedDate,
            messages: this.props.messages
        }));

        this.props.onDateChange(this.props.name, newDateStr);
    };

    getIsDateBeyondThreshold = () => {
        let selectedDateIsAfterMaxDate = false;
        let selectedDateIsBeforeMinDate = false;

        if (this.props.maxDate !== null) {
            selectedDateIsAfterMaxDate = moment(this.state.selectedDate).isAfter(
                this.props.maxDate
            );
        }

        if (this.props.minDate !== null) {
            selectedDateIsBeforeMinDate = moment(this.state.selectedDate).isBefore(
                this.props.minDate
            );
        }
        return selectedDateIsAfterMaxDate || selectedDateIsBeforeMinDate;
    };

    handleBlur = () => {
        let selectedDateIsAfterMaxDate = false;
        let selectedDateIsBeforeMinDate = false;

        if (this.props.maxDate !== null) {
            selectedDateIsAfterMaxDate = moment(this.state.selectedDate).isAfter(
                this.props.maxDate
            );
        }

        if (this.props.minDate !== null) {
            selectedDateIsBeforeMinDate = moment(this.state.selectedDate).isBefore(
                this.props.minDate
            );
        }

        if (selectedDateIsAfterMaxDate || selectedDateIsBeforeMinDate) {
            this.setState({
                value: "",
                selectedDate: null,
                messages: [
                    {
                        type: "error",
                        text: this.props.dateErrorMessage
                    }
                ]
            });
            return this.props.onDateBlur(this.props.name, null);
        }

        this.props.onDateBlur(this.props.name, this.state.selectedDate);
    };

    handleShowCalendar = event => {
        this.setState({ isShowingCalendar: true });
    };

    handleHideCalendar = event => {
        this.setState(({ selectedDate, disabledDates, value }) => ({
            isShowingCalendar: false,
            value: selectedDate ? this.formatDate(selectedDate) : value
        }));
    };

    handleValidateDate = event => {
        this.setState(({ selectedDate, value }) => {
            // We don't have a selectedDate but we have a value. That means that the value
            // could not be parsed and so the date is invalid
            if (!selectedDate && value) {
                return {
                    messages: [{ type: "error", text: "This date is invalid" }]
                };
            }
            // Display a message if the user has typed in a value that corresponds to a
            // disabledDate
            if (this.isDisabledDate(parseDate(selectedDate))) {
                return {
                    messages: [{ type: "error", text: "This date is disabled" }]
                };
            }
        });
    };

    handleDayClick = (event, { date }) => {
        if (this.props.maxDate !== null) {
            const selectedDateIsAfterMaxDate = moment(date).isAfter(
                this.props.maxDate
            );

            const beforeMinDate = moment(date).isBefore(this.props.minDate)

            if (beforeMinDate) {
                this.setState({
                    value: "",
                    selectedDate: null,
                    messages: [
                        {
                            type: "error",
                            text: this.props.minDateErrorMessage ? this.props.minDateErrorMessage : this.props.dateErrorMessage
                        }
                    ]
                });
                return;
            }
            if (selectedDateIsAfterMaxDate) {
                this.setState({
                    value: "",
                    selectedDate: null,
                    messages: [
                        {
                            type: "error",
                            text: this.props.maxDateErrorMessage ? this.props.maxDateErrorMessage : this.props.dateErrorMessage
                        }
                    ]
                });
                return;
            }
        }

        if (this.props.minDate !== null) {
            const selectedDateIsBeforeMinDate = moment(date).isBefore(
                this.props.minDate
            );
            if (selectedDateIsBeforeMinDate) {
                this.setState({
                    value: "",
                    selectedDate: null,
                    messages: [
                        {
                            type: "error",
                            text: this.props.dateErrorMessage
                        }
                    ]
                });
                return;
            }
        }

        this.setState({
            selectedDate: date,
            renderedDate: date,
            messages: this.props.messages
        });
        this.props.onDateSelected(this.props.name, date);
    };

    handleSelectNextDay = event => {
        this.modifySelectedDate("day", 1);
    };

    handleSelectPrevDay = event => {
        this.modifySelectedDate("day", -1);
    };

    handleRenderNextMonth = event => {
        this.modifyRenderedDate("month", 1);
    };

    handleRenderPrevMonth = event => {
        this.modifyRenderedDate("month", -1);
    };

    modifyRenderedDate = (type, step) => {
        this.setState(({ renderedDate }) => {
            return { renderedDate: this.modifyDate(renderedDate, type, step) };
        });
    };

    modifySelectedDate = (type, step) => {
        this.setState(({ selectedDate, renderedDate }) => {
            // We are either going to increase or decrease our selectedDate by 1 day.
            // If we do not have a selectedDate yet, we'll just select the first day of
            // the currently rendered month instead.
            const newDate = selectedDate
                ? this.modifyDate(selectedDate, type, step)
                : parseDate(renderedDate)
                    .startOf("month")
                    .toISOString();

            return {
                selectedDate: newDate,
                renderedDate: newDate,
                value: this.formatDate(newDate),
                messages: this.props.messages
            };
        });
    };

    modifyDate = (dateStr, type, step) => {
        const date = parseDate(dateStr);
        date.add(step, type);
        return date.toISOString();
    };

    isDisabledDate = (date, disabledDates = this.state.disabledDates) => {
        return disabledDates.reduce((result, disabledDate) => {
            return result || date.isSame(disabledDate, "day");
        }, false);
    };

    renderWeekdayLabels = () => {
        const date = parseDate(this.state.renderedDate).startOf("week");

        return Array.apply(null, Array(7)).map(() => {
            const currentDate = date.clone();
            date.add(1, "day");

            return (
                <AccessibleContent alt={currentDate.format("dddd")}>
                    {currentDate.format("dd")}
                </AccessibleContent>
            );
        });
    };

    renderDays() {
        const { renderedDate, selectedDate, todayDate } = this.state;

        return this.generateMonth().map(date => {
            const dateStr = date.toISOString();

            return (
                <DateInput.Day
                    key={dateStr}
                    date={dateStr}
                    layout={"stacked"}
                    interaction={this.isDisabledDate(date) ? "disabled" : "enabled"}
                    isSelected={date.isSame(selectedDate, "day")}
                    isToday={date.isSame(todayDate, "day")}
                    isOutsideMonth={!date.isSame(renderedDate, "month")}
                    label={`${date.format("D")} ${date.format("MMMM")} ${date.format(
                        "YYYY"
                    )}`}
                    onClick={this.handleDayClick}
                >
                    {date.format("D")}
                </DateInput.Day>
            );
        });
    }

    render() {
        const { value, isShowingCalendar, messages } = this.state;
        const { label, size, id, name, placeholder, isDisabled } = this.props;

        const date = parseDate(this.state.renderedDate);

        const buttonProps = (type = "prev") => ({
            size: "small",
            withBackground: false,
            withBorder: false,
            renderIcon:
                type === "prev" ? (
                    <IconArrowOpenStartSolid color="primary" />
                ) : (
                    <IconArrowOpenEndSolid color="primary" />
                ),
            screenReaderLabel: type === "prev" ? "Previous month" : "Next month"
        });

        const { isLoading } = this.props;
        return (
            <div className={classnames(styles.FormDateInput)}>
                <FormInputLoader hide={!isLoading} />

                <DateInput
                    renderLabel={label}
                    assistiveText="Type a date or use arrow keys to navigate date picker."
                    value={value}
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    size={size}
                    messages={messages}
                    interaction={isLoading || isDisabled ? "disabled" : "enabled"}
                    placement={"bottom end"}
                    placeholder={placeholder}
                    isShowingCalendar={isShowingCalendar}
                    onRequestValidateDate={this.handleValidateDate}
                    onRequestShowCalendar={this.handleShowCalendar}
                    onRequestHideCalendar={this.handleHideCalendar}
                    onRequestSelectNextDay={this.handleSelectNextDay}
                    onRequestSelectPrevDay={this.handleSelectPrevDay}
                    onRequestRenderNextMonth={this.handleRenderNextMonth}
                    onRequestRenderPrevMonth={this.handleRenderPrevMonth}
                    renderNavigationLabel={
                        <span>
              <div>{date.format("MMMM")}</div>
              <div>{date.format("YYYY")}</div>
            </span>
                    }
                    renderPrevMonthButton={<IconButton {...buttonProps("prev")} />}
                    renderNextMonthButton={<IconButton {...buttonProps("next")} />}
                    renderWeekdayLabels={this.renderWeekdayLabels()}
                    name={name}
                    id={id}
                >
                    {this.renderDays()}
                </DateInput>
            </div>
        );
    }
}

export default IasFormDateInputRestart;